'use client';

import { c as _c } from "react/compiler-runtime";
import { activityLook, buildJobGroups, jobSummary, phaseLook, updateLabel, updateLook } from '@shared/all/helpers/runningJobs';
import { Bell, Layers, Plus, TerminalSquare } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useJobTerminal } from '../../hooks/useJobTerminal';
import { useRunners } from '../../hooks/useRunners';
import Indicator from './JobIndicator';
const ROW = 'flex w-full items-center gap-2 rounded px-1 py-[3px] text-left';

/**
 * Every live job across every board. Rendered inline — in the left rail on
 * wide screens, and on /spaces where the rail is hidden — so the fleet has one
 * home instead of a floating panel parked over the page.
 *
 * Each row is one line: which board, which card or job, and whether it is
 * working. Phase, mode, and runner live in the row's tooltip — with a dozen
 * jobs running, a third line each pushed the list off the screen.
 */
const RunningJobs = () => {
  const $ = _c(49);
  const {
    sessions,
    activities,
    runners,
    updateState
  } = useRunners();
  const {
    openJob,
    openJobKey,
    launchSession
  } = useJobTerminal();
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = pathname?.match(/^\/s\/([^/?#]+)/);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const m = t0;
  const currentSlug = m ? decodeURIComponent(m[1]) : null;
  const [notifyPermission, setNotifyPermission] = useState("unsupported");
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (typeof window === "undefined" || !("Notification" in window)) {
        return;
      }
      setNotifyPermission(Notification.permission);
    };
    t2 = [];
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== activities || $[5] !== currentSlug || $[6] !== launchSession || $[7] !== notifyPermission || $[8] !== openJob || $[9] !== openJobKey || $[10] !== runners || $[11] !== sessions || $[12] !== updateState) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t10;
      if ($[20] !== updateState) {
        t10 = r => updateState[r.runnerId];
        $[20] = updateState;
        $[21] = t10;
      } else {
        t10 = $[21];
      }
      const updatingRunners = runners.filter(t10);
      const jobCount = sessions.length + activities.length;
      const showRunner = runners.length > 1;
      if (jobCount === 0 && runners.length === 0) {
        t9 = null;
        break bb0;
      }
      const groups = buildJobGroups(sessions, activities, currentSlug);
      t5 = "w-full min-w-0";
      let t11;
      if ($[22] !== jobCount || $[23] !== runners.length) {
        t11 = jobSummary(jobCount, runners.length);
        $[22] = jobCount;
        $[23] = runners.length;
        $[24] = t11;
      } else {
        t11 = $[24];
      }
      let t12;
      if ($[25] !== t11) {
        t12 = <span>{t11}</span>;
        $[25] = t11;
        $[26] = t12;
      } else {
        t12 = $[26];
      }
      let t13;
      if ($[27] !== notifyPermission) {
        t13 = notifyPermission === "default" && <button type="button" onClick={() => void Notification.requestPermission().then(setNotifyPermission)} title="Get a browser notification when a job finishes while this tab is in the background" className="flex shrink-0 items-center gap-1 rounded px-1 transition-colors hover:text-textPrimary"><Bell size={11} />Notify me</button>;
        $[27] = notifyPermission;
        $[28] = t13;
      } else {
        t13 = $[28];
      }
      if ($[29] !== t12 || $[30] !== t13) {
        t6 = <div className="flex items-center justify-between gap-2 px-1 text-[11px] text-textSecondary">{t12}{t13}</div>;
        $[29] = t12;
        $[30] = t13;
        $[31] = t6;
      } else {
        t6 = $[31];
      }
      t7 = jobCount === 0 && updatingRunners.length === 0 && <p className="px-1 pt-1.5 text-[12px] text-textSecondary/70">No jobs running.</p>;
      let t14;
      if ($[32] !== updateState) {
        t14 = r_0 => {
          const state = updateState[r_0.runnerId];
          const look = updateLook(state);
          return <div key={`update-${r_0.runnerId}`} title={look.label} className={ROW}><Indicator tone={look.tone} working={look.working} /><span className="min-w-0 flex-1 truncate text-[12px] text-textPrimary">{updateLabel(r_0.name, state)}</span></div>;
        };
        $[32] = updateState;
        $[33] = t14;
      } else {
        t14 = $[33];
      }
      t8 = updatingRunners.map(t14);
      t3 = "flex flex-col";
      let t15;
      if ($[34] !== currentSlug || $[35] !== launchSession || $[36] !== openJob || $[37] !== openJobKey || $[38] !== showRunner) {
        t15 = group => <div key={group.key} className="min-w-0">{(() => {
            const header = <><Layers size={10} className="shrink-0 opacity-70" /><span className="min-w-0 truncate">{group.slug ?? "Unattributed"}</span>{group.slug === currentSlug && <span className="shrink-0 rounded bg-primary/15 px-1 text-[9px] font-semibold text-primary">HERE</span>}</>;
            const slug = group.slug;
            if (!slug) {
              return <div className="flex items-center gap-1.5 px-1 pb-0.5 pt-2 text-[10px] font-medium uppercase tracking-wider text-textSecondary">{header}</div>;
            }
            const runnerId = group.sessions[0]?.runnerId ?? group.activities[0]?.runnerId;
            return <div className="flex items-end gap-1"><Link href={`/s/${slug}`} title={`Open ${slug}`} className="flex items-center gap-1.5 px-1 pb-0.5 pt-2 text-[10px] font-medium uppercase tracking-wider text-textSecondary min-w-0 flex-1 rounded transition-colors hover:text-textPrimary">{header}</Link><button type="button" data-id="running-jobs-launch" onClick={() => launchSession({
                spaceSlug: slug,
                runnerId
              })} title={`New session on ${slug}`} aria-label={`New session on ${slug}`} className="flex shrink-0 items-center rounded p-0.5 text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary"><TerminalSquare size={12} /><Plus size={9} /></button></div>;
          })()}{group.activities.map(a => {
            const look_0 = activityLook(a.state);
            const row = <><Indicator tone={look_0.tone} working={look_0.working} /><span className="min-w-0 flex-1 truncate text-[12px] text-textPrimary">{a.label}</span>{showRunner && <span className="shrink-0 truncate text-[10px] text-textSecondary">{a.runnerName}</span>}</>;
            const title = [look_0.label, a.detail, a.runnerName].filter(Boolean).join(" \xB7 ");
            return a.spaceSlug ? <Link key={`${a.spaceSlug}::${a.id}`} href={`/s/${a.spaceSlug}`} title={title} className={`${ROW} transition-colors hover:bg-background-light`}>{row}</Link> : <div key={a.id} title={title} className={ROW}>{row}</div>;
          })}{group.sessions.map(s => {
            const look_1 = phaseLook(s.phase, s.agentState);
            const key = `${s.spaceSlug}::${s.postId}`;
            const openable = Boolean(s.spaceSlug);
            return <button key={key} type="button" disabled={!openable} onClick={() => openJob({
              spaceSlug: s.spaceSlug,
              postId: s.postId,
              title: s.taskTitle,
              mode: s.mode,
              runnerId: s.runnerId
            })} title={`${look_1.label} · ${s.mode} · ${s.runnerName}`} className={`${ROW} ${openJobKey === key ? "bg-background-light" : ""} ${openable ? "transition-colors hover:bg-background-light" : "cursor-default"}`}><Indicator tone={look_1.tone} working={look_1.working} /><span className="min-w-0 flex-1 truncate text-[12px] text-textPrimary">{s.taskTitle}</span>{showRunner && <span className="shrink-0 truncate text-[10px] text-textSecondary">{s.runnerName}</span>}</button>;
          })}</div>;
        $[34] = currentSlug;
        $[35] = launchSession;
        $[36] = openJob;
        $[37] = openJobKey;
        $[38] = showRunner;
        $[39] = t15;
      } else {
        t15 = $[39];
      }
      t4 = groups.map(t15);
    }
    $[4] = activities;
    $[5] = currentSlug;
    $[6] = launchSession;
    $[7] = notifyPermission;
    $[8] = openJob;
    $[9] = openJobKey;
    $[10] = runners;
    $[11] = sessions;
    $[12] = updateState;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[40] !== t3 || $[41] !== t4) {
    t10 = <div className={t3}>{t4}</div>;
    $[40] = t3;
    $[41] = t4;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== t10 || $[44] !== t5 || $[45] !== t6 || $[46] !== t7 || $[47] !== t8) {
    t11 = <div className={t5}>{t6}{t7}{t8}{t10}</div>;
    $[43] = t10;
    $[44] = t5;
    $[45] = t6;
    $[46] = t7;
    $[47] = t8;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  return t11;
};
export default RunningJobs;